import { AvailablePaymentCurrency, formatCurrencyFromCents } from "@simplyk/common";

import { isCurrentUserTopBidder } from "./bid-status.helper";

import { AuctionBidBadge } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/AuctionRateCard/AuctionBidBadgeChip";
import { useLocaleField } from "@/canvas/features/FormV2/helpers/useLocaleField";
import {
  FrontendAuctionContextValues,
  HighestProductBidsPerRateObject,
} from "@/common/contexts/FrontendAuctionContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { MINIMUM_BID_INCREMENT } from "@/common/helpers/form-edition";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { RateOutput } from "@/common/types/trpc";

interface UseAuctionDetailDrawerParams {
  rate: RateOutput;
  formCurrency: AvailablePaymentCurrency;
  ticketingId: string;
  rateData: HighestProductBidsPerRateObject | undefined;
  commandBidsRateIdsMap: FrontendAuctionContextValues["commandBidsRateIdsMap"];
  hasAlreadyBid: boolean;
  isDrawerOpen: boolean;
  currentUserCommandIds?: readonly string[];
}

export interface BidHistoryEntry {
  bidderName: string | null;
  commandId: string;
  amount: number;
  createdAtUtc: string;
  isAutoBid: boolean;
}

export interface UseAuctionDetailDrawerResult {
  title: string;
  description: string;
  photoUrls: string[];
  currentBidAmountInCents: number;
  rateHasABid: boolean;
  topBidderName: string | undefined;
  minimumBidLabel: string;
  minimumBidAmountInCents: number;
  formattedBidIncrement: string;
  bidBadge: AuctionBidBadge | undefined;
  bidHistory: BidHistoryEntry[];
}

export function useAuctionDetailDrawer({
  rate,
  formCurrency,
  ticketingId,
  rateData,
  commandBidsRateIdsMap,
  hasAlreadyBid,
  isDrawerOpen,
  currentUserCommandIds,
}: UseAuctionDetailDrawerParams): UseAuctionDetailDrawerResult {
  const getLocaleField = useLocaleField();
  const { isoLocale } = useLocaleContext();
  const { t } = useTranslate();

  const rateField = getLocaleField(rate.rateFields);
  const title = rateField?.title ?? "";
  const description = rateField?.description ?? "";

  const highestBidAmount = rateData?.amount;
  const currentBidAmountInCents = highestBidAmount ?? rate.amount;

  const rateHasABid = highestBidAmount !== undefined && highestBidAmount !== null;
  const bidIncrement = rate.bidIncrement || MINIMUM_BID_INCREMENT;
  const minimumBidAmountInCents = currentBidAmountInCents + (rateHasABid ? bidIncrement : 0);

  const formattedBidIncrement = formatCurrencyFromCents(bidIncrement, {
    isoLocale,
    currency: formCurrency,
  });

  const formattedMinimumBid = formatCurrencyFromCents(minimumBidAmountInCents, {
    isoLocale,
    currency: formCurrency,
  });
  const minimumBidLabel = t("ticketing", "auctionV2.drawer.minimumBid", { amount: formattedMinimumBid });

  const userBidAmount = commandBidsRateIdsMap?.[rate.id]?.amount;
  const userHasBid = hasAlreadyBid && userBidAmount !== undefined && userBidAmount !== null;

  const userIsTopBidder = isCurrentUserTopBidder(rateData, currentUserCommandIds);

  const bidBadge: AuctionBidBadge | undefined = userHasBid ? (userIsTopBidder ? "isWinning" : "isOutbid") : undefined;

  const topBidderName = userIsTopBidder
    ? t("ticketing", "auctionV2.drawer.you")
    : (rateData?.topBidderName ?? (rateHasABid ? t("ticketing", "auctionV2.drawer.anonymous") : undefined));

  const { data: bidHistoryData } = trpc.form_getBidHistoryForRate.useQuery(
    { ticketingId, rateId: rate.id },
    { enabled: Boolean(ticketingId && rate.id), refetchInterval: isDrawerOpen ? 5000 : false }
  );

  const bidHistory: BidHistoryEntry[] = (bidHistoryData ?? []).map((entry) => ({
    bidderName: entry.bidderName,
    commandId: entry.commandId,
    amount: entry.amount,
    createdAtUtc: entry.createdAtUtc as unknown as string,
    isAutoBid: entry.isAutoBid,
  }));

  return {
    title,
    description,
    photoUrls: rate.photoUrls ?? [],
    currentBidAmountInCents,
    rateHasABid,
    topBidderName,
    minimumBidLabel,
    minimumBidAmountInCents,
    formattedBidIncrement,
    bidBadge,
    bidHistory,
  };
}
